import { useUnit } from 'effector-react'
import { SetURLSearchParams } from 'react-router-dom'
import { Tag, Typography } from 'antd'
import Table, { ColumnsType, TableProps } from 'antd/es/table'
import {
  useColumnLocalSort,
  useColumnSearch,
  useTableSearchParams,
} from 'src/libs'
import { $countriesList } from 'src/features/dictionaries/model'
import { transformCountriesToOptions } from 'src/libs/formatCountries'
import {
  GatewayStatus,
  PaymentTraffic,
} from 'src/pages/billing/paymentTrafficMonitoring/types'
import { useMonitoringList } from 'src/pages/billing/paymentTrafficMonitoring/hook'
import { processingProvider } from 'src/libs/processingProvider'
import { FilterDropdownNumberRange } from 'src/pages/billing/paymentTrafficMonitoring/atoms'
import {
  MapGatewayStatusAntd,
  STATUS_OPTIONS,
} from 'src/pages/billing/paymentTrafficMonitoring/const'

type Props = {
  searchParams: URLSearchParams
  setSearchParams: SetURLSearchParams
}

export const TableMonitoring = ({ searchParams, setSearchParams }: Props) => {
  const countriesList = useUnit($countriesList)

  const { monitoringList, loadingMonitoringList, totalCount } =
    useMonitoringList()

  const { Text } = Typography

  const getNumberParam = (name: string): number | undefined => {
    const value = searchParams.get(name)

    if (value === null || value.trim() === '') return undefined

    const number = Number(value)

    return Number.isFinite(number) ? number : undefined
  }

  const filterFields = ['country', 'ps', 'status']
  const sorterFields = ['status', 'gatewayId']
  const searchFields = ['gatewayId']

  const handleTableChange: TableProps<PaymentTraffic>['onChange'] =
    useTableSearchParams({
      searchFields,
      filterFields,
      sorterFields,
      usePagination: true,
    })

  const gatewayIdSearch = useColumnSearch<PaymentTraffic>({
    dataIndex: 'gatewayId',
    placeholder: 'gatewayId',
  })

  const statusFilter = searchParams.get('filterField_status')

  const columns: ColumnsType<PaymentTraffic> = [
    {
      title: 'Country',
      dataIndex: 'country',
      filterMultiple: true,
      filterSearch: true,
      filters: transformCountriesToOptions(countriesList),
      defaultFilteredValue:
        searchParams.get('filterField_country')?.split(',').filter(Boolean) ??
        [],
    },
    {
      title: 'Processing Provider',
      dataIndex: 'ps',
      filterMultiple: true,
      filterSearch: true,
      filters: processingProvider,
      defaultFilteredValue:
        searchParams.get('filterField_ps')?.split(',').filter(Boolean) ?? [],
    },
    {
      title: 'GatewayId',
      dataIndex: 'gatewayId',
      key: 'gatewayId',
      sorter: true,
      ...useColumnLocalSort<PaymentTraffic>({
        dataIndex: 'gatewayId',
      }),
      ...gatewayIdSearch,
    },
    {
      title: 'Gateway Name',
      key: 'gatewayName',
      render: (record) => <>{record.gatewayName ?? record.internalName}</>,
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
      align: 'center',
      render: (item) => <Text type="secondary">{item}</Text>,
    },
    {
      title: 'All request',
      dataIndex: 'allRequest',
      key: 'allRequest',
    },
    {
      title: 'Accepted',
      dataIndex: 'accepted',
      key: 'accepted',
    },
    {
      title: 'Pending_PS',
      dataIndex: 'pendingPs',
      key: 'pendingPs',
    },
    {
      title: 'Vendor error',
      dataIndex: 'vendorError',
      key: 'vendorError',
    },
    {
      title: 'Other statuses',
      dataIndex: 'otherStatuses',
      key: 'otherStatuses',
    },
    {
      title: '% Accepted',
      dataIndex: 'acceptedPct',
      key: 'acceptedPct',
      render: (item) => (
        <>
          {item} <Text type="secondary">%</Text>
        </>
      ),
    },
    {
      title: '% Pending PS',
      dataIndex: 'pendingPsPct',
      key: 'pendingPsPct',
      render: (item) => (
        <>
          {item} <Text type="secondary">%</Text>
        </>
      ),
    },
    {
      title: 'Pending amount, USD',
      dataIndex: 'pendingAmountUsd',
      key: 'pendingAmountUsd',
      filterOnClose: false,
      filtered:
        searchParams.has('pendingAmountUsdFrom') ||
        searchParams.has('pendingAmountUsdTo'),
      filterDropdown: ({ close }) => (
        <FilterDropdownNumberRange
          from="pendingAmountUsdFrom"
          to="pendingAmountUsdTo"
          initialValues={{
            from: getNumberParam('pendingAmountUsdFrom'),
            to: getNumberParam('pendingAmountUsdTo'),
          }}
          setSearchParams={setSearchParams}
          close={close}
        />
      ),
      render: (value) => value,
    },
    {
      title: '% Vendor error',
      dataIndex: 'vendorErrorPct',
      key: 'vendorErrorPct',
      filterOnClose: false,
      filtered:
        searchParams.has('vendorErrorPctFrom') ||
        searchParams.has('vendorErrorPctTo'),
      filterDropdown: ({ close }) => (
        <FilterDropdownNumberRange
          from="vendorErrorPctFrom"
          to="vendorErrorPctTo"
          initialValues={{
            from: getNumberParam('vendorErrorPctFrom'),
            to: getNumberParam('vendorErrorPctTo'),
          }}
          setSearchParams={setSearchParams}
          close={close}
        />
      ),
      render: (item) => (
        <>
          {item} <Text type="secondary">%</Text>
        </>
      ),
    },
    {
      title: 'Status Gateway',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      sorter: true,
      ...useColumnLocalSort<PaymentTraffic>({
        dataIndex: 'status',
        initialValue: 'descend',
      }),
      filters: STATUS_OPTIONS,
      filterMultiple: false,
      filteredValue: statusFilter ? [statusFilter] : undefined,
      render: (status: GatewayStatus) => (
        <>
          {status ? (
            <Tag
              color={MapGatewayStatusAntd[status]}
              style={{ color: 'black' }}
            >
              {status}
            </Tag>
          ) : null}
        </>
      ),
    },
  ]

  const current = Number(searchParams.get('page')) || 1
  const pageSize = Number(searchParams.get('limit')) || 50
  return (
    <Table
      dataSource={monitoringList}
      bordered={true}
      scroll={{ x: true }}
      loading={loadingMonitoringList}
      rowKey={(record, i) => String(record.gatewayId) + String(i)}
      columns={columns}
      pagination={{
        current,
        pageSize,
        showSizeChanger: true,
        total: totalCount,
        position: ['bottomCenter'],
      }}
      onChange={handleTableChange}
    />
  )
}
